import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, MenuItem, TextField,
  Button, CircularProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Stack, Alert, Paper, LinearProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Tooltip, InputAdornment, Tab, Tabs, Divider,
  FormControlLabel, Switch, Badge, Avatar
} from '@mui/material';
import {
  Assignment, Add, Edit, Delete, Search, Refresh,
  CheckCircle, Warning, Schedule, BarChart, Class,
  Send, Grading, FilterList, FileDownload, Person,
  AccessTime, Flag, Comment
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { settingsAPI } from '../services/api';
import { exportCSV } from '../utils/export';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api' });
api.interceptors.request.use(c => {
  const t = localStorage.getItem('sms_token');
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});

const PRIORITY_CFG = {
  low:    { label: 'Low',    color: 'default' },
  normal: { label: 'Normal', color: 'primary' },
  high:   { label: 'High',   color: 'error'   },
};
const STATUS_CFG = {
  pending:   { label: 'Pending',   color: 'warning' },
  submitted: { label: 'Submitted', color: 'success' },
  late:      { label: 'Late',      color: 'warning' },
  missing:   { label: 'Missing',   color: 'error'   },
  excused:   { label: 'Excused',   color: 'default' },
};
const fmtD = d => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const fmtDT = d => d ? new Date(d).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';
const daysUntil = d => d ? Math.ceil((new Date(d) - new Date()) / 86400000) : null;

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

// ── Assign Homework Dialog ─────────────────────────────────────────────────────
function AssignDialog({ open, onClose, onSave, editing, classes, subjects, academicYears }) {
  const today = new Date().toISOString().split('T')[0];
  const BLANK = {
    title: '', description: '', subject_id: '', class_id: '',
    academic_year_id: '', assigned_date: today, due_date: '', due_time: '23:59',
    estimated_duration: '', priority: 'normal', allow_late_submit: false
  };
  const [form, setForm] = useState(BLANK);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target?.value ?? e }));

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        title: editing.title || '',
        description: editing.description || '',
        subject_id: editing.subject_id || '',
        class_id: editing.class_id || '',
        academic_year_id: editing.academic_year_id || '',
        assigned_date: editing.assigned_date?.split('T')[0] || today,
        due_date: editing.due_date?.split('T')[0] || '',
        due_time: editing.due_time?.slice(0,5) || '23:59',
        estimated_duration: editing.estimated_duration || '',
        priority: editing.priority || 'normal',
        allow_late_submit: !!editing.allow_late_submit,
      });
    } else {
      const cur = academicYears.find(y => y.is_current);
      setForm({ ...BLANK, academic_year_id: cur?.id || '' });
    }
  }, [open, editing]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ bgcolor: '#0f2b4a', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
        <Assignment /> {editing ? 'Edit Homework' : 'Assign Homework'}
      </DialogTitle>
      <DialogContent dividers sx={{ p: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField fullWidth size="small" label="Title *" value={form.title} onChange={set('title')}
              placeholder="e.g. Chapter 5 Exercise 3 — Quadratic Equations" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth size="small" label="Subject *" value={form.subject_id} onChange={set('subject_id')}>
              <MenuItem value="">Select subject…</MenuItem>
              {subjects.map(s => <MenuItem key={s.id} value={s.id}>{s.name}{s.code ? ` (${s.code})` : ''}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth size="small" label="Class *" value={form.class_id} onChange={set('class_id')}>
              <MenuItem value="">Select class…</MenuItem>
              {classes.map(c => <MenuItem key={c.id} value={c.id}>{c.name}{c.stream ? ` (${c.stream})` : ''}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField select fullWidth size="small" label="Academic Year *" value={form.academic_year_id} onChange={set('academic_year_id')}>
              {academicYears.map(y => <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term}{y.is_current ? ' ✓' : ''}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField select fullWidth size="small" label="Priority" value={form.priority} onChange={set('priority')}>
              {Object.entries(PRIORITY_CFG).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" type="number" label="Est. Duration (minutes)" value={form.estimated_duration}
              onChange={set('estimated_duration')} placeholder="e.g. 30"
              InputProps={{ endAdornment: <InputAdornment position="end">min</InputAdornment> }} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" type="date" label="Assigned Date" value={form.assigned_date}
              onChange={set('assigned_date')} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" type="date" label="Due Date *" value={form.due_date}
              onChange={set('due_date')} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" type="time" label="Due Time" value={form.due_time}
              onChange={set('due_time')} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" label="Instructions / Description" value={form.description}
              onChange={set('description')} multiline rows={4}
              placeholder="Describe the assignment in detail. Include page numbers, question ranges, or any special instructions…" />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={<Switch checked={form.allow_late_submit} onChange={e => setForm(f => ({ ...f, allow_late_submit: e.target.checked }))} />}
              label={<Typography fontSize={13}>Allow late submissions after due date</Typography>}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(form)} startIcon={editing ? <Edit /> : <Send />} sx={{ bgcolor: '#0f2b4a' }}>
          {editing ? 'Update' : 'Assign to Class'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Submission Tracker Dialog ──────────────────────────────────────────────────
function SubmissionsDialog({ open, onClose, homework, onUpdate }) {
  const { enqueueSnackbar } = useSnackbar();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [comment, setComment] = useState({ student_id: '', text: '' });

  useEffect(() => {
    if (open && homework) {
      setLoading(true);
      api.get(`/homework/${homework.id}`)
        .then(r => { if (r.data.success) setDetail(r.data.data); })
        .finally(() => setLoading(false));
    }
  }, [open, homework]);

  const updateSub = async (subId, updates) => {
    try {
      await api.put(`/homework/submissions/${subId}`, updates);
      // Refresh
      const r = await api.get(`/homework/${homework.id}`);
      if (r.data.success) setDetail(r.data.data);
      enqueueSnackbar('Updated', { variant: 'success' });
      onUpdate();
    } catch { enqueueSnackbar('Error', { variant: 'error' }); }
  };

  const addComment = async (studentId) => {
    if (!comment.text.trim()) return;
    try {
      await api.post('/homework/comments', { homework_id: homework.id, student_id: studentId, comment: comment.text });
      setComment({ student_id: '', text: '' });
      const r = await api.get(`/homework/${homework.id}`);
      if (r.data.success) setDetail(r.data.data);
      enqueueSnackbar('Comment added', { variant: 'success' });
    } catch { enqueueSnackbar('Error', { variant: 'error' }); }
  };

  if (!homework) return null;

  const submitted = detail?.submissions?.filter(s => ['submitted','late'].includes(s.status)).length || 0;
  const total = detail?.submissions?.length || 0;
  const pct = total > 0 ? Math.round((submitted / total) * 100) : 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ bgcolor: '#0f2b4a', color: 'white' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography fontWeight={700}>{homework.title}</Typography>
            <Typography fontSize={12} sx={{ opacity: 0.7 }}>{homework.subject_name} · {homework.class_name}{homework.stream ? ` (${homework.stream})` : ''} · Due: {fmtD(homework.due_date)}</Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Chip label={`${submitted}/${total} submitted`} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
            <Chip label={`${pct}%`} size="small" color={pct >= 80 ? 'success' : pct >= 50 ? 'warning' : 'error'} />
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {loading ? <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box> : detail && (
          <>
            <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
              <LinearProgress variant="determinate" value={pct}
                color={pct >= 80 ? 'success' : pct >= 50 ? 'warning' : 'error'}
                sx={{ height: 8, borderRadius: 4 }} />
            </Box>
            <TableContainer sx={{ maxHeight: 480 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {['Student', 'Status', 'Submitted At', 'Mark /100', 'Feedback', 'Actions'].map(h => (
                      <TableCell key={h} sx={{ bgcolor: '#f5f6fa', fontWeight: 700, fontSize: 11 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detail.submissions?.map((sub, i) => {
                    const cfg = STATUS_CFG[sub.status] || STATUS_CFG.pending;
                    return (
                      <TableRow key={sub.id} hover sx={{ bgcolor: sub.status === 'missing' ? '#fff3f3' : i % 2 === 0 ? 'white' : '#fafafa' }}>
                        <TableCell>
                          <Typography fontSize={12} fontWeight={600}>{sub.last_name}, {sub.first_name}</Typography>
                          <Typography fontSize={10} color="text.secondary">{sub.admission_number}</Typography>
                        </TableCell>
                        <TableCell>
                          <TextField select size="small" value={sub.status} sx={{ minWidth: 110 }}
                            onChange={e => updateSub(sub.id, { status: e.target.value, mark: sub.mark, feedback: sub.feedback })}>
                            {Object.entries(STATUS_CFG).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
                          </TextField>
                        </TableCell>
                        <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>{sub.submitted_at ? fmtDT(sub.submitted_at) : '—'}</TableCell>
                        <TableCell>
                          <TextField size="small" type="number" value={sub.mark ?? ''} sx={{ width: 70 }}
                            inputProps={{ min: 0, max: 100 }}
                            onChange={e => updateSub(sub.id, { status: sub.status, mark: e.target.value, feedback: sub.feedback })}
                            placeholder="—" />
                        </TableCell>
                        <TableCell>
                          <TextField size="small" value={sub.feedback || ''} sx={{ minWidth: 160 }}
                            placeholder="Feedback…"
                            onBlur={e => { if (e.target.value !== (sub.feedback || '')) updateSub(sub.id, { status: sub.status, mark: sub.mark, feedback: e.target.value }); }} />
                        </TableCell>
                        <TableCell>
                          <Tooltip title={`Add comment for ${sub.first_name}`}>
                            <IconButton size="small" onClick={() => setComment({ student_id: sub.id_student || sub.student_id, text: '' })}>
                              <Comment fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Overview Tab ───────────────────────────────────────────────────────────────
function OverviewTab({ academicYears, user }) {
  const { enqueueSnackbar } = useSnackbar();
  const [stats, setStats]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setYear] = useState('');

  useEffect(() => { const cur = academicYears.find(y => y.is_current); if (cur) setYear(cur.id); }, [academicYears]);
  useEffect(() => {
    if (!selectedYear) return;
    setLoading(true);
    const params = { academic_year_id: selectedYear };
    if (user?.teacher_id) params.teacher_id = user.teacher_id;
    api.get('/homework/stats', { params })
      .then(r => { if (r.data.success) setStats(r.data.data); })
      .catch(() => enqueueSnackbar('Failed to load stats', { variant: 'error' }))
      .finally(() => setLoading(false));
  }, [selectedYear, user, enqueueSnackbar]);

  if (loading) return <CircularProgress />;
  const s = stats?.summary;

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <TextField select size="small" label="Academic Year" value={selectedYear} onChange={e => setYear(e.target.value)} sx={{ minWidth: 200 }}>
          {academicYears.map(y => <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term}{y.is_current ? ' ✓' : ''}</MenuItem>)}
        </TextField>
      </Box>
      {s && <>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Total Assignments', value: s.total_assignments || 0, color: '#0f2b4a' },
            { label: 'Submitted', value: s.total_submitted || 0, color: '#2e7d32' },
            { label: 'Missing', value: s.total_missing || 0, color: '#c62828' },
            { label: 'Late', value: s.total_late || 0, color: '#e65100' },
            { label: 'Pending', value: s.total_pending || 0, color: '#1565c0' },
          ].map(card => (
            <Grid item xs={6} sm key={card.label}>
              <Card variant="outlined" sx={{ borderRadius: 2, borderLeft: `4px solid ${card.color}` }}>
                <CardContent sx={{ pb: '12px !important' }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" fontSize={9}>{card.label}</Typography>
                  <Typography variant="h4" fontWeight={800} sx={{ color: card.color }}>{card.value}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={2}>
          {/* Upcoming */}
          <Grid item xs={12} sm={6}>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography fontWeight={700} gutterBottom>⏰ Upcoming Due Dates</Typography>
                {!stats.upcoming?.length && <Typography fontSize={12} color="success.main">No upcoming homework — all clear!</Typography>}
                {stats.upcoming?.map((h, i) => {
                  const days = daysUntil(h.due_date);
                  return (
                    <Box key={h.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.8, borderBottom: '1px solid #f0f0f0' }}>
                      <Box>
                        <Typography fontSize={13} fontWeight={600}>{h.title}</Typography>
                        <Typography fontSize={11} color="text.secondary">{h.subject_name} · {h.class_name}{h.stream ? ` (${h.stream})` : ''}</Typography>
                      </Box>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        {h.priority === 'high' && <Flag sx={{ fontSize: 14, color: 'error.main' }} />}
                        <Chip
                          label={days === 0 ? 'Today!' : days === 1 ? 'Tomorrow' : `${days}d`}
                          size="small"
                          color={days <= 1 ? 'error' : days <= 3 ? 'warning' : 'default'}
                          sx={{ fontSize: 10, fontWeight: 700 }}
                        />
                        <Chip label={`${h.pending_count} pending`} size="small" color="primary" sx={{ fontSize: 10 }} />
                      </Stack>
                    </Box>
                  );
                })}
              </CardContent>
            </Card>
          </Grid>

          {/* By subject */}
          <Grid item xs={12} sm={6}>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography fontWeight={700} gutterBottom>📚 By Subject</Typography>
                {stats.by_subject?.map(b => {
                  const total = (b.submitted || 0) + (b.missing || 0);
                  const pct = total > 0 ? Math.round((b.submitted / total) * 100) : 0;
                  return (
                    <Box key={b.subject_name} sx={{ mb: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                        <Typography fontSize={12} fontWeight={600}>{b.subject_name}</Typography>
                        <Typography fontSize={11} color="text.secondary">{b.assignments} assignments · {pct}% done</Typography>
                      </Box>
                      <LinearProgress variant="determinate" value={pct}
                        color={pct >= 80 ? 'success' : pct >= 50 ? 'warning' : 'error'}
                        sx={{ height: 6, borderRadius: 3 }} />
                    </Box>
                  );
                })}
              </CardContent>
            </Card>
          </Grid>

          {/* Most missing students */}
          {stats.most_missing?.length > 0 && (
            <Grid item xs={12}>
              <Card variant="outlined" sx={{ borderRadius: 2, border: '1px solid #fca5a5' }}>
                <CardContent>
                  <Typography fontWeight={700} gutterBottom color="error">⚠ Students with Most Missing Homework</Typography>
                  <Stack direction="row" spacing={2} flexWrap="wrap">
                    {stats.most_missing?.map((st, i) => (
                      <Paper key={i} variant="outlined" sx={{ px: 2, py: 1, borderRadius: 2, textAlign: 'center' }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: '#c62828', fontSize: 13, mx: 'auto', mb: 0.5 }}>
                          {st.first_name?.[0]}{st.last_name?.[0]}
                        </Avatar>
                        <Typography fontSize={12} fontWeight={600}>{st.first_name} {st.last_name}</Typography>
                        <Typography fontSize={10} color="text.secondary">{st.admission_number}</Typography>
                        <Chip label={`${st.missing_count} missing`} size="small" color="error" sx={{ mt: 0.5, fontSize: 10 }} />
                      </Paper>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </>}
    </Box>
  );
}

// ── Assignments Tab ────────────────────────────────────────────────────────────
function AssignmentsTab({ academicYears, classes, subjects, user }) {
  const { enqueueSnackbar } = useSnackbar();
  const [homework, setHomework]   = useState([]);
  const [loading, setLoading]     = useState(false);
  const [selectedYear, setYear]   = useState('');
  const [filterClass, setClass]   = useState('');
  const [filterSubject, setSubj]  = useState('');
  const [search, setSearch]       = useState('');
  const [dialog, setDialog]       = useState(false);
  const [editing, setEditing]     = useState(null);
  const [subsDialog, setSubs]     = useState(null);

  const load = useCallback(async () => {
    if (!selectedYear) return;
    setLoading(true);
    try {
      const params = { academic_year_id: selectedYear };
      if (filterClass)   params.class_id   = filterClass;
      if (filterSubject) params.subject_id  = filterSubject;
      // If teacher, scope to their assignments
      if (user?.teacher_id && !['admin','registrar'].includes(user?.role)) {
        params.teacher_id = user.teacher_id;
      }
      const r = await api.get('/homework', { params });
      if (r.data.success) setHomework(r.data.data);
    } catch { enqueueSnackbar('Failed to load', { variant: 'error' }); }
    finally { setLoading(false); }
  }, [selectedYear, filterClass, filterSubject, user, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const cur = academicYears.find(y => y.is_current); if (cur) setYear(cur.id); }, [academicYears]);

  const save = async (form) => {
    try {
      if (editing) {
        await api.put(`/homework/${editing.id}`, form);
        enqueueSnackbar('Homework updated', { variant: 'success' });
      } else {
        const r = await api.post('/homework', form);
        enqueueSnackbar(r.data.message, { variant: 'success' });
      }
      setDialog(false); setEditing(null); load();
    } catch (err) { enqueueSnackbar(err.response?.data?.message || 'Error', { variant: 'error' }); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this homework assignment?')) return;
    try { await api.delete(`/homework/${id}`); enqueueSnackbar('Deleted', { variant: 'success' }); load(); }
    catch (err) { enqueueSnackbar(err.response?.data?.message || 'Cannot delete', { variant: 'error' }); }
  };

  const filtered = homework.filter(h => !search ||
    `${h.title} ${h.subject_name} ${h.class_name}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box>
      {/* Filters */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={3}>
          <TextField select size="small" fullWidth label="Academic Year" value={selectedYear} onChange={e => setYear(e.target.value)}>
            {academicYears.map(y => <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term}{y.is_current ? ' ✓' : ''}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={6} sm={2}>
          <TextField select size="small" fullWidth label="Class" value={filterClass} onChange={e => setClass(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {classes.map(c => <MenuItem key={c.id} value={c.id}>{c.name}{c.stream ? ` (${c.stream})` : ''}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={6} sm={2}>
          <TextField select size="small" fullWidth label="Subject" value={filterSubject} onChange={e => setSubj(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {subjects.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={5}>
          <TextField size="small" fullWidth placeholder="Search title, subject or class…" value={search} onChange={e => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
        </Grid>
      </Grid>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap">
        <Chip label={`${filtered.length} assignment(s)`} size="small" />
        <Chip label={`${filtered.filter(h => daysUntil(h.due_date) !== null && daysUntil(h.due_date) <= 2 && daysUntil(h.due_date) >= 0).length} due soon`} size="small" color="warning" />
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title="Refresh"><IconButton size="small" onClick={load}><Refresh /></IconButton></Tooltip>
        <Button size="small" variant="outlined" startIcon={<FileDownload />} onClick={() =>
          exportCSV('homework', ['Title', 'Subject', 'Class', 'Assigned', 'Due', 'Priority', 'Submitted', 'Missing', 'Total'],
            filtered.map(h => [h.title, h.subject_name, `${h.class_name}${h.stream?` (${h.stream})`:''}`,
              fmtD(h.assigned_date), fmtD(h.due_date), h.priority,
              h.submitted_count||0, h.missing_count||0, h.total_students||0]))
        }>Export</Button>
        <Button variant="contained" startIcon={<Add />} onClick={() => { setEditing(null); setDialog(true); }} sx={{ bgcolor: '#0f2b4a' }}>
          Assign Homework
        </Button>
      </Stack>

      {loading ? <CircularProgress /> : filtered.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
          <Assignment sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">No homework assignments found. Click "Assign Homework" to create one.</Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {filtered.map(h => {
            const days = daysUntil(h.due_date);
            const isOverdue = days !== null && days < 0;
            const isDueSoon = days !== null && days >= 0 && days <= 2;
            const submitted = parseInt(h.submitted_count || 0) + parseInt(h.late_count || 0);
            const total = parseInt(h.total_students || 0);
            const pct = total > 0 ? Math.round((submitted / total) * 100) : 0;
            const cfg = PRIORITY_CFG[h.priority] || PRIORITY_CFG.normal;
            return (
              <Grid item xs={12} sm={6} md={4} key={h.id}>
                <Card variant="outlined" sx={{ borderRadius: 2,
                  borderLeft: `4px solid ${isOverdue ? '#c62828' : isDueSoon ? '#e65100' : '#0f2b4a'}`,
                  bgcolor: isOverdue ? '#fff8f8' : 'white' }}>
                  <CardContent sx={{ pb: '12px !important' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
                      <Box sx={{ flexGrow: 1, mr: 1 }}>
                        <Typography fontSize={14} fontWeight={700} sx={{ lineHeight: 1.3 }}>{h.title}</Typography>
                        <Typography fontSize={11} color="text.secondary">{h.subject_name} · {h.class_name}{h.stream ? ` (${h.stream})` : ''}</Typography>
                      </Box>
                      <Chip label={cfg.label} size="small" color={cfg.color} sx={{ fontSize: 10, flexShrink: 0 }} />
                    </Stack>

                    <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                      <AccessTime sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography fontSize={11} color={isOverdue ? 'error.main' : isDueSoon ? 'warning.main' : 'text.secondary'}
                        fontWeight={isOverdue || isDueSoon ? 700 : 400}>
                        {isOverdue ? `Overdue (${Math.abs(days)}d ago)` : isDueSoon ? `Due ${days === 0 ? 'today' : 'tomorrow'}` : `Due ${fmtD(h.due_date)}`}
                      </Typography>
                    </Stack>

                    {total > 0 && (
                      <Box sx={{ mb: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                          <Typography fontSize={11} color="text.secondary">Submissions</Typography>
                          <Typography fontSize={11} fontWeight={600}>{submitted}/{total} ({pct}%)</Typography>
                        </Box>
                        <LinearProgress variant="determinate" value={pct}
                          color={pct >= 80 ? 'success' : pct >= 50 ? 'warning' : 'error'}
                          sx={{ height: 5, borderRadius: 3 }} />
                        <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                          {h.missing_count > 0 && <Chip label={`${h.missing_count} missing`} size="small" color="error" sx={{ fontSize: 9, height: 16 }} />}
                          {h.pending_count > 0 && <Chip label={`${h.pending_count} pending`} size="small" color="warning" sx={{ fontSize: 9, height: 16 }} />}
                          {h.late_count > 0 && <Chip label={`${h.late_count} late`} size="small" color="warning" sx={{ fontSize: 9, height: 16 }} />}
                        </Stack>
                      </Box>
                    )}

                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title="View submissions & mark">
                        <Button size="small" variant="outlined" startIcon={<Grading />} onClick={() => setSubs(h)} sx={{ fontSize: 11 }}>
                          Mark
                        </Button>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => { setEditing(h); setDialog(true); }}><Edit fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => del(h.id)}><Delete fontSize="small" /></IconButton>
                      </Tooltip>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <AssignDialog open={dialog} onClose={() => { setDialog(false); setEditing(null); }}
        onSave={save} editing={editing} classes={classes} subjects={subjects} academicYears={academicYears} />
      <SubmissionsDialog open={!!subsDialog} onClose={() => setSubs(null)} homework={subsDialog} onUpdate={load} />
    </Box>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function HomeworkTracker() {
  const [tab, setTab]           = useState(0);
  const [academicYears, setAY]  = useState([]);
  const [classes, setClasses]   = useState([]);
  const [subjects, setSubjects] = useState([]);
  const { user }                = useAuth();

  useEffect(() => {
    settingsAPI.getAcademicYears().then(r => { if (r.data.success) setAY(r.data.data); }).catch(() => {});
    settingsAPI.getClasses().then(r => { if (r.data.success) setClasses(r.data.data); }).catch(() => {});
    api.get('/subjects').then(r => { if (r.data.success) setSubjects(r.data.data); }).catch(() => {});
  }, []);

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Assignment color="primary" /> Homework Tracker
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Assign homework · Track submissions · Mark & give feedback · Parent visibility
        </Typography>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Tab icon={<BarChart fontSize="small" />} label="Overview" iconPosition="start" sx={{ fontSize: 12 }} />
        <Tab icon={<Assignment fontSize="small" />} label="Assignments" iconPosition="start" sx={{ fontSize: 12 }} />
      </Tabs>

      <TabPanel value={tab} index={0}><OverviewTab academicYears={academicYears} user={user} /></TabPanel>
      <TabPanel value={tab} index={1}><AssignmentsTab academicYears={academicYears} classes={classes} subjects={subjects} user={user} /></TabPanel>
    </Box>
  );
}
